import {
  CallDataSourceDto,
  CreateDepartmentDto,
  DepartmentDto,
  OrganizationGroupDto,
  UpdateDepartmentDto,
} from "@dialogics/shared/dto/types"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { ModalProps } from "@component/modal"
import { EntitySelect } from "@component/entity-select"
import { dataSourcesApi } from "../data-sources/service"
import { organizationGroupApi } from "../organization-group/organization-group-service"
import { useCreateDepartmentMutation, useUpdateDepartmentMutation } from "./department-service"

type DepartmentFormProps = {
  department?: DepartmentDto
  organizationId: number
} & ModalProps

export const DepartmentForm = ({ isOpen, onClose, department, organizationId }: DepartmentFormProps) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CreateDepartmentDto | UpdateDepartmentDto>()

  const [selectedCallDataSource, setSelectedCallDataSource] = useState<CallDataSourceDto | null>(null)
  const [selectedOrgGroup, setSelectedOrgGroup] = useState<OrganizationGroupDto | null>(null)

  const [createDepartment, { isLoading: isCreating }] = useCreateDepartmentMutation()
  const [updateDepartment, { isLoading: isUpdating }] = useUpdateDepartmentMutation()

  useEffect(() => {
    if (department) {
      reset({ title: department.title })
      setSelectedCallDataSource(
        department.call_data_source
          ? ({ id: department.call_data_source.id, type: department.call_data_source.type } as CallDataSourceDto)
          : null,
      )
      setSelectedOrgGroup(
        department.organization_group
          ? ({ id: department.organization_group.id, code: department.organization_group.code } as OrganizationGroupDto)
          : null,
      )
      setValue("call_data_source_id", department.call_data_source_id)
      setValue("group_id", department.group_id)
    } else {
      reset({ title: "" })
      setSelectedCallDataSource(null)
      setSelectedOrgGroup(null)
    }
  }, [department, reset, setValue])

  const onSubmit = async (data: CreateDepartmentDto | UpdateDepartmentDto) => {
    if (department) {
      await updateDepartment({ id: department.id, ...data })
    } else {
      await createDepartment({ organization_id: organizationId, ...data } as CreateDepartmentDto)
    }
    onClose()
  }

  return (
    <dialog className={`modal ${isOpen ? "modal-open" : ""}`}>
      <div className="modal-box">
        <h3 className="font-bold text-lg">{department ? "Редактировать департамент" : "Создать департамент"}</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Название</legend>
            <input
              {...register("title", { required: "Название обязательно" })}
              className={`input input-bordered w-full ${errors.title ? "input-error" : ""}`}
            />
            {errors.title && <span className="text-error text-xs">{errors.title.message}</span>}
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Источник данных</legend>
            <EntitySelect<CallDataSourceDto>
              action={dataSourcesApi.endpoints.callDataSources}
              getActionParams={() => ({ organization_id: organizationId })}
              defaultOptions
              isValidNewOption={() => false}
              getOptionValue={opt => String(opt.id)}
              getOptionLabel={opt => `#${opt.id} — ${opt.type}`}
              value={selectedCallDataSource}
              onChange={opt => {
                setSelectedCallDataSource((opt as CallDataSourceDto) ?? null)
                setValue("call_data_source_id", (opt as CallDataSourceDto)?.id ?? null)
              }}
              placeholder="Выберите источник данных..."
              isClearable
            />
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Конфигурация</legend>
            <EntitySelect<OrganizationGroupDto>
              action={organizationGroupApi.endpoints.getOrganizationGroups}
              getActionParams={() => ({ organizationId })}
              defaultOptions
              isValidNewOption={() => false}
              getOptionValue={opt => String(opt.id)}
              getOptionLabel={opt => opt.code}
              value={selectedOrgGroup}
              onChange={opt => {
                setSelectedOrgGroup((opt as OrganizationGroupDto) ?? null)
                setValue("group_id", (opt as OrganizationGroupDto)?.id ?? null)
              }}
              placeholder="Выберите конфигурацию..."
              isClearable
            />
          </fieldset>

          <div className="modal-action">
            <button type="button" className="btn" onClick={onClose} disabled={isCreating || isUpdating}>
              Отмена
            </button>
            <button type="submit" className="btn btn-primary" disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? "Сохранение..." : "Сохранить"}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  )
}
